import { Close, Search } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useReducer, useState } from "react";
import { CONTACTS_APIS, DETAILS_APIS, MASTER_DATA_APIS } from "../../../../common/apis";
import Form, { FormActions } from "../../../../components/Form";
import FormFooter from "../../../../components/FormFooter";
import useFetch from "../../../../hooks/useFetch";
import { useToast } from "../../../../rootContext";
import appResources from "../../../../app-resources/en/Resources.json";
import renderCellExpand from "../../../../components/DatatableCellExpand";
import NewDataTable, { NewDataGridAction } from "../../../../components/NewDataTable";

export default function SearchUnit({
  actions,
  contactId,
  propId,
  rowSelected,
  selectedUnit,
}) {

  const { get, post } = useFetch();
  const { showWarningToast } = useToast();
  const [unitsData, setUnitsData] = useState([]);
  const [data, dispatch] = useReducer(SearchFluidsFilterReducer, searchFilterDefaults);
  const [allProducts, setAllProducts] = useState([]);

  const tblOptions = {
    columns: GetColumns(actions),
    dataSource: unitsData,

    getRowId: (originalRow) => originalRow.UnitId,
    enableColumnActions: false,
    sx: { height: "50dvh", },

    muiTableBodyCellProps: ({ cell, column, row, table }) => {
      return {
        sx:
          cell.column.columnDef.accessorKey === "Product" && row.original.Product ? {
            backgroundColor: (row?.original?.ProductColour + "!important") || "",
            color: "white",
            py: 0.25,
            border: "0.5px solid #d0d0d0",
          } : {
            py: 0.25,
            border: "0.5px solid #d0d0d0",
          }
      }
    },

    muiTableBodyProps: {
      sx: (theme) => ({
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]) > td':
          { backgroundColor: "inherit" },
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          { backgroundColor: "inherit" },
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]) > td':
          { backgroundColor: "#e2f2ff" },
        // backgroundColor: "inherit",//
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          { backgroundColor: "#e2f2ff" },
      }),

      cellStylingHandler: "productCommonCellColour",
      rowsSelected: function (rows) {
        rowSelected && rowSelected(rows[0]);
      },
    }
  };

  useEffect(() => {
    (async function () {
      const response = await get(`${MASTER_DATA_APIS.GET_BY_TYPES}?types=Products`);
      //Products with AllProducts added at 0 index
      const allProd = JSON.parse(JSON.stringify(response.Products));
      if (allProd) {
        allProd.unshift({
          ProductId: 0,
          Product: "All Products",
          Colour: "",
        });
      }
      setAllProducts(allProd || {});
    })();

    if (selectedUnit) {
      dispatch({
        payload: {
          ProductId: selectedUnit?.ProductId,
          Customer: selectedUnit?.Customer,
          Site: selectedUnit.Site,
          UseStartsWith: false,
          _key: Date.now(),
        },
        type: FormActions.LOAD_DATA,
      });
    }
  }, [selectedUnit]);

  const formActions = {
    clear: function () {
      dispatch({
        payload: {
          ...searchFilterDefaults,
          _key: Date.now(),
        },
        type: FormActions.RESET_FORM,
      });
    },
    search: async function () {
      if (contactId) {
        const tempUnitsData = await post(CONTACTS_APIS.UNITS_FOR_CONTACT, {
          ...data,
          ContactId: contactId,
          ExcludeScoped: (propId === 2) ? true : false,
          UseStartsWith: data?.UseStartsWith === true ? true : false,
          ShowAll: true,
        });
        if (tempUnitsData.length > 0) {
          setUnitsData(tempUnitsData);
        } else {
          showWarningToast(appResources.NO_MATCHING_UNITES_WERE_FOUND_MSG);
          setUnitsData(tempUnitsData);
        }
      } else {
        const tempUnitsData = await post(DETAILS_APIS.SEARCH_UNITS, {
          ...data,
        });
        if (tempUnitsData.length > 0) {
          let filteredData = tempUnitsData.filter(item => item?.UnitId !== selectedUnit?.UnitId);
          setUnitsData(filteredData);
        } else {
          showWarningToast(appResources.NO_MATCHING_UNITES_WERE_FOUND_MSG);
          setUnitsData(tempUnitsData);
        }
      }
    },
  };

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (data?.Customer || data?.Site || data?.Unit || data?.Unit || data?.Make || data?.Model || data?.Serial) {
          formActions?.search();
        }
      }
    }, [data]);

  useEffect(() => {
    document.addEventListener("keyup", handleKeyPress);
    return () => {
      document.removeEventListener("keyup", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <>
      <Grid container size={800}>
        <Grid item xs={12}>
          <Form
            sections={GetFormSections(allProducts)}
            data={data}
            dispatch={dispatch}
            key={data && data._key}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: -5 }}>
          <FormFooter
            buttons={GetFormButtons(data, formActions)}
            hideDivider
            gmt={0}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">
            Total Records: <b> {unitsData && unitsData.length}</b>
          </Typography>
          <NewDataTable {...tblOptions} />
        </Grid>
      </Grid>
    </>
  );
}
const GetFormButtons = (data, { clear, search }) => [
  {
    variant: "outlined",
    label: "Clear",
    onClick: clear,
    startIcon: <Close />,
  },
  {
    variant: "contained",
    label: "Search",
    onClick: search,
    disabled: !(data?.Customer || data?.Site || data?.Unit || data?.Unit || data?.Make || data?.Model || data?.Serial),
    startIcon: <Search />,
  },
];

function GetColumns(actions) {
  return [
    {
      header: "Actions",
      size: 45,
      hide: !actions || !actions.length,
      Cell: function ({ row }) {
        return (actions || []).map(function (ac, i) {
          return (
            <NewDataGridAction
              minWidth={"45px"}
              row={row?.original}
              onClick={ac.action}
              label={ac.label}
            // startIcon={ac.startIcon}
            />
          );
        });
      },
    },
    {
      header: "Product",
      accessorKey: "Product",
      size: 120,
    },
    {
      header: "Customer",
      accessorKey: "Customer",
      size: 200,
      Cell: ({ renderedCellValue }) => renderCellExpand({ value: renderedCellValue }),
    },
    {
      header: "Site",
      accessorKey: "Site",
      size: 150,
      Cell: ({ renderedCellValue }) => renderCellExpand({ value: renderedCellValue }),
    },
    {
      header: "Unit",
      accessorKey: "Unit",
      size: 150,
      Cell: ({ renderedCellValue }) => renderCellExpand({ value: renderedCellValue }),
    },
    {
      header: "Model",
      accessorKey: "UnitModelFormatted",
      size: 150,
      Cell: ({ renderedCellValue }) => renderCellExpand({ value: renderedCellValue }),
    },
    {
      header: "Serial",
      accessorKey: "Serial",
      size: 120,
      Cell: ({ renderedCellValue }) => renderCellExpand({ value: renderedCellValue }),
    },
    {
      header: "Predelivery",
      accessorKey: "Predelivery",
      enableSorting: false,
      size: 60,
    },

    {
      header: "Parked",
      accessorKey: "Parked",
      enableSorting: false,
      size: 50,
    },
    {
      header: "Scrapped",
      accessorKey: "Scrapped",
      enableSorting: false,
      size: 50,
    },
    {
      header: "Archived",
      accessorKey: "Archived",
      enableSorting: false,
      size: 50,
    },
  ];
}

function GetFormSections(allProducts) {
  return [
    {
      xsCol: 12,
      fields: [
        {
          xsCol: 2,
          name: "ProductId",
          label: "Product",
          type: "dropdown",
          required: true,
          selectOptions: (allProducts || []).map((x) => ({
            value: x.ProductId,
            text: x.Product,
          })),
        },
        {
          xsCol: 2,
          name: "Customer",
          label: "Customer",
        },
        {
          xsCol: 2,
          name: "Site",
          label: "Site",
        },
        {
          xsCol: 2,
          name: "Unit",
          label: "Unit",
        },
        {
          xsCol: 2,
          name: "Make",
          label: "Make",
        },
        {
          xsCol: 2,
          name: "Model",
          label: "Model",
        },
        {
          xsCol: 2,
          name: "Serial",
          label: "Serial",
        },
        {
          xsCol: 4,
          name: "UseStartsWith",
          label: "Starts With Search",
          type: "switch",
          //  hidden: contactId ? false : true,
        },
      ],
    },
  ];
}

const searchFilterDefaults = {
  ProductId: 0,
  UseStartsWith: false,
};
function SearchFluidsFilterReducer(state, action) {
  if (action.type === FormActions.INPUT_CHANGED) {
    return { ...state, [action.payload.name]: action.payload.value };
  }
  if (action.type === FormActions.LOAD_DATA) {
    return { ...action.payload };
  }
  if (action.type === FormActions.RESET_FORM) {
    return { ...action.payload };
  }
  return state;
}
